import { store } from "../../_Bootstrap/RoutingAndStore";
import type { User } from "../../Actions/Users/UserActions";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { getLogLevel, LogLevel } from "../../Utilities/Platform/Logging";
import { subscribeToOption } from "../../Utilities/Platform/Options/Options";
import Request from "../../Utilities/Platform/Request";
import type { DarkModeListener } from "./DarkModeHooks";

export class DarkModeManager {
    protected darkMode = false;
    protected currentUser: User | { id: false; };
    protected darkModeOption: "light" | "dark" | "auto";
    protected mediaDarkMode: boolean;

    protected listeners = new Set<DarkModeListener>();

    constructor() {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const state = store.getState();

        this.currentUser = state.user;
        this.mediaDarkMode = mediaQuery.matches;
        this.darkModeOption = subscribeToOption(
            "register_dark_mode",
            this.optionChange
        );

        store.subscribe(this.storeChange);

        mediaQuery.addEventListener("change", this.mediaChange);

        this.update();
    }

    protected storeChange = (): void => {
        const state = store.getState();

        if(this.currentUser.id !== state.user.id) {
            this.currentUser = state.user;

            this.update();
        }
    };

    protected optionChange = (option: "register_dark_mode", value: "light" | "dark" | "auto"): void => {
        this.darkModeOption = value;

        this.update();
    };

    protected mediaChange = (e: MediaQueryListEvent): void => {
        this.mediaDarkMode = e.matches;

        this.update();
    };

    protected determineDarkMode = (): boolean => {
        let option = this.darkModeOption;

        if(this.currentUser.id) {
            if(this.currentUser.darkMode !== "default" && !!this.currentUser.darkMode) {
                option = this.currentUser.darkMode;
            }
        }

        if(option === "auto") {
            return this.mediaDarkMode;
        }

        return option === "dark";
    };

    protected update = (): void => {
        const darkMode = this.determineDarkMode();

        this.setDarkMode(darkMode);

        for(const listener of this.listeners) {
            listener();
        }
    };

    protected setDarkMode = (enabled: boolean): void => {
        if(this.darkMode === enabled) {
            return;
        }

        this.darkMode = enabled;

        if(enabled) {
            document.body.classList.add("dark-mode", "dark");
        } else {
            document.body.classList.remove("dark-mode", "dark");
        }
    };

    public setUserDarkMode = async (value: "dark" | "light" | "default" | "auto"): Promise<void> => {
        if(!this.currentUser.id) {
            return;
        }

        this.currentUser.darkMode = value;
        this.update();

        try {
            const results = await Request.post("/graphql", {
                query: `
                    mutation UpdateUser(
                        $id: ID!,
                        $darkMode: UserDarkModeEnum
                    ) {
                        updateUser(
                            id: $id,
                            darkMode: $darkMode
                        ) {
                            id,
                        },
                    },
                `,
                variables: {
                    id      : this.currentUser.id,
                    darkMode: value.toUpperCase(),
                },
            });

            if(results.data.errors) {
                throw results.data.errors;
            }
        } catch(e) {
            console.error("Error updating user's theme", e);
            throw new Error("Unable to update user setting");
        }

        try {
            const source = await determineDataSource();

            await source.users.update(this.currentUser.id, {
                dark_mode: value,
            });
        } catch(e) {
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.error("Unable to update the local database with the user's theme preferences", e);
            }
        }
    };

    public addListener = (listener: DarkModeListener): void => {
        this.listeners.add(listener);
    };

    public removeListener = (listener: DarkModeListener): void => {
        this.listeners.delete(listener);
    };

    public isDarkMode = (): boolean => {
        return this.darkMode;
    };
}
